import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { CurrentUserService } from 'app/core/user/current-user.service';
import { of, switchMap } from 'rxjs';

/**
 * The two routes an expired session may still reach.
 *
 * `change-password` is the way out of that state and `sign-out` is the way to abandon it; every
 * other authenticated route sends the member to the first of those. Mirrors the exemption
 * `/api/auth/change-password` is registered with on the backend — that one is the boundary, this
 * one only saves the member from a screen full of 403s.
 */
const EXPIRED_PASSWORD_ALLOWED = ['/change-password', '/sign-out'];

export const AuthGuard: CanActivateFn | CanActivateChildFn = (route, state) =>
{
    const router: Router = inject(Router);
    const currentUser: CurrentUserService = inject(CurrentUserService);

    // Check the authentication status
    return inject(AuthService).check().pipe(
        switchMap((authenticated) =>
        {
            // If the user is not authenticated...
            if ( !authenticated )
            {
                // Redirect to the sign-in page with a redirectUrl param
                const redirectURL = state.url === '/sign-out' ? '' : `redirectURL=${state.url}`;
                const urlTree = router.parseUrl(`sign-in?${redirectURL}`);

                return of(urlTree);
            }

            // A signed-in member whose password has expired is pinned to the screen that fixes
            // it. This lives here rather than in a guard of its own because AuthGuard is already
            // canActivateChild on every authenticated route group — including the one the whole
            // application sits in — so a route added later cannot forget it.
            if ( currentUser.passwordExpired() && !isAllowedWhileExpired(state.url) )
            {
                return of(router.parseUrl('/change-password'));
            }

            // Allow the access
            return of(true);
        }),
    );
};

/** Matched on the path alone, so a query string or fragment does not defeat the exemption. */
function isAllowedWhileExpired(url: string): boolean
{
    const path = url.split(/[?#]/)[0];

    return EXPIRED_PASSWORD_ALLOWED.includes(path);
}
